import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Upload } from "tus-js-client";
import { useTus } from "use-tus";

import { type FileWithMetaType, parseFile, toString } from "@/hooks/UseTusHooks/Generics.tsx";
import { UploadTurns } from "@/hooks/UseTusHooks/uploadTurns";
import { AppContext } from "@/models/AppStateProvider.tsx";
import { DataContext } from "@/models/DataProvider.tsx";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { fetchUploadConfig } from "@/utils/recording";

/** A file the upload service holds: the ids a message carries it by, and its name for display. */
export type TusUploaded = { contentId: string; itemId: string; name?: string };

/** One file's way to the upload service, and how it went. */
export type TusUpload = {
  /** Reads `file`'s details and uploads it. */
  start: (file: File) => Promise<void>;
  /** Uploads a file whose details the caller already read, as the drop zone does once it has asked for them. */
  startParsed: (data: FileWithMetaType) => void;
  uploading: boolean;
  uploaded: boolean;
  error: string | null;
  /** Drops the upload in flight and clears how the last one went. */
  reset: () => void;
};

/**
 * Uploads a file over tus to `feedId` and reports its ids once the upload service holds it.
 *
 * One file at a time: a start aborts the upload before it, whose outcome is then never reported. `onUploaded` is the
 * one current when the upload started, so a caller that changes channel meanwhile still hears about the file under
 * the channel it was chosen for.
 *
 * @param feedId - The channel the upload is tagged with.
 * @param onUploaded - Told the file's ids when the upload succeeds.
 * @param abortOnUnmount - Whether the upload stops when the caller unmounts. A caller whose `onUploaded` writes
 * somewhere that outlives it passes false, so the file still lands.
 */
export function useTusUpload({
  feedId,
  onUploaded,
  abortOnUnmount = true,
}: {
  feedId?: string;
  onUploaded?: (uploaded: TusUploaded) => void;
  abortOnUnmount?: boolean;
}): TusUpload {
  const { config } = useContext(AppContext);
  const { tusUrl, stage } = fetchUploadConfig(config);
  const { preferredLanguage } = useContext(DataContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { upload, setUpload, remove } = useTus({ autoAbort: abortOnUnmount });
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turns] = useState(() => new UploadTurns());
  const startedUpload = useRef<Upload | null>(null);

  // use-tus hands the upload back a render after `setUpload`; start each one as it arrives.
  useEffect(() => {
    if (!upload || upload === startedUpload.current) return;
    startedUpload.current = upload;
    upload.start();
  }, [upload]);

  const send = useCallback(
    (data: FileWithMetaType, turn: number) => {
      const stringValues = toString(data.meta);
      const metadata = {
        ...stringValues,
        feedItemId: data.meta.itemId,
        feedId: feedId ?? "",
        accountId: myAccountId,
        myAccount: myAccountId,
        stage,
        preferredLanguage,
        startTime: new Date().toISOString(),
        isSilent: true,
        filename: data.meta.name,
        filetype: data.meta.filetype,
      };
      const { contentId, itemId, filename } = data.meta;
      setUpload(data.file, {
        storeFingerprintForResuming: false,
        removeFingerprintOnSuccess: true,
        endpoint: tusUrl,
        metadata,
        onSuccess: turns.guard(turn, () => {
          setUploading(false);
          setUploaded(true);
          onUploaded?.({ contentId, itemId, name: filename });
        }),
        onError: turns.guard(turn, (uploadError: Error) => {
          setUploading(false);
          setError(uploadError?.message || "There was an error uploading");
        }),
      });
    },
    [feedId, myAccountId, stage, preferredLanguage, tusUrl, setUpload, onUploaded, turns],
  );

  // Abandons the upload before this one, if any, and returns this one's turn.
  const begin = useCallback(() => {
    const turn = turns.next();
    remove();
    setUploading(true);
    setUploaded(false);
    setError(null);
    return turn;
  }, [remove, turns]);

  const startParsed = useCallback((data: FileWithMetaType) => send(data, begin()), [send, begin]);

  const start = useCallback(
    async (file: File) => {
      const turn = begin();
      const data = await parseFile(file).catch(() => undefined);
      // A later start replaced this one while the file was read.
      if (!turns.isCurrent(turn)) return;
      if (!data?.meta) {
        setUploading(false);
        setError("Invalid File, please try again.");
        return;
      }
      send(data, turn);
    },
    [send, begin, turns],
  );

  const reset = useCallback(() => {
    turns.next();
    remove();
    setUploading(false);
    setUploaded(false);
    setError(null);
  }, [remove, turns]);

  return { start, startParsed, uploading, uploaded, error, reset };
}
